import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, Clock3, Search, Users, Clock } from 'lucide-react';

import { DataTable } from '@/components/shared/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { FilterBar } from '@/components/shared/FilterBar';
import { KpiCard } from '@/components/shared/KpiCard';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { customerFulfillmentService } from '@/services/customerFulfillment/customerFulfillment.service';
import type {
  CustomerFulfillmentKpis,
  FulfillmentStatus,
  OrderSummary,
  TopCustomerAtRisk,
} from '@/services/customerFulfillment/customerFulfillment.types';
import { CustomerFulfillmentDetailSidebar } from '../components/CustomerFulfillmentDetailSidebar';

const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function isOverdueDate(value: string | null): boolean {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export function CustomerFulfillmentPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();

  const [kpis, setKpis] = useState<CustomerFulfillmentKpis | null>(null);
  const [topCustomers, setTopCustomers] = useState<TopCustomerAtRisk[]>([]);
  const [rows, setRows] = useState<OrderSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingKpis, setLoadingKpis] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<
    'all' | 'attention' | 'at_risk' | 'needs_purchase'
  >('attention');

  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Debounce de busqueda
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // KPIs + top clientes (snapshot global cacheado en backend)
  useEffect(() => {
    let cancelled = false;
    setLoadingKpis(true);
    Promise.all([
      customerFulfillmentService.getKpis(warehouseId ?? undefined),
      customerFulfillmentService.getTopCustomers(10, warehouseId ?? undefined),
    ])
      .then(([kpisRes, topRes]) => {
        if (cancelled) return;
        setKpis(kpisRes.kpis);
        setTopCustomers(topRes.results);
      })
      .catch((err) => {
        console.error('[CustomerFulfillment] kpis/top error:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingKpis(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId]);

  const fetchPage = async (nextPage: number, pageSizeOverride?: number) => {
    const effectivePageSize = pageSizeOverride ?? pageSize;
    setLoadingList(true);
    try {
      const res = await customerFulfillmentService.listOrders({
        page: nextPage,
        page_size: effectivePageSize,
        search: debouncedSearch || undefined,
        risk_filter: riskFilter,
        include_overdue: true,
        warehouse_id: warehouseId ?? undefined,
      });
      setRows(res.results);
      setPage(res.page);
      setTotalPages(res.total_pages || 1);
      setTotalCount(res.count || 0);
    } catch (err) {
      console.error('[CustomerFulfillment] list error:', err);
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoadingList(false);
    }
  };

  // Cuando cambian filtros volvemos a la pagina 1
  useEffect(() => {
    void fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, riskFilter, warehouseId]);

  const filterOptions = useMemo(
    () => [
      {
        id: 'risk',
        name: t('customerFulfillment.filters.risk'),
        options: [
          { value: 'attention', label: t('customerFulfillment.filters.attention') },
          { value: 'at_risk', label: t('customerFulfillment.filters.onlyAtRisk') },
          { value: 'needs_purchase', label: t('customerFulfillment.filters.onlyNeedsPurchase') },
          { value: 'all', label: t('customerFulfillment.filters.all') },
        ],
      },
    ],
    [t]
  );

  const activeFilters = useMemo(() => {
    const opt = filterOptions[0].options.find((o) => String(o.value) === riskFilter);
    return opt
      ? [{ id: 'risk', label: opt.label, value: riskFilter }]
      : [];
  }, [filterOptions, riskFilter]);

  const handleFilterChange = (next: Record<string, string[]>) => {
    if (Object.prototype.hasOwnProperty.call(next, 'risk')) {
      const v = next.risk?.[0];
      if (v === 'all' || v === 'at_risk' || v === 'needs_purchase' || v === 'attention') {
        setRiskFilter(v);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        header: t('customerFulfillment.table.orderNumber'),
        accessor: 'order_number',
        cell: (v: string) => <span className="font-mono text-gray-900">{v || '—'}</span>,
      },
      {
        header: t('customerFulfillment.table.customer'),
        accessor: 'customer',
        cell: (v: string) => (
          <span className="text-gray-900 font-medium" title={v}>
            {v || '—'}
          </span>
        ),
      },
      {
        header: t('customerFulfillment.table.expectedDelivery'),
        accessor: 'expected_delivery_date',
        cell: (v: string | null) => {
          const overdue = isOverdueDate(v);
          return (
            <span className={`whitespace-nowrap ${overdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
              {formatDate(v)}
              {overdue && (
                <span className="ml-1 inline-flex items-center text-[10px] text-red-700 align-middle">
                  <Clock className="w-3 h-3 mr-0.5" />
                  {t('customerFulfillment.table.overdue')}
                </span>
              )}
            </span>
          );
        },
      },
      {
        header: t('customerFulfillment.table.linesEvaluated'),
        accessor: 'n_lines_evaluated',
        cell: (v: number, item: OrderSummary) => (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-900 font-medium">{v ?? 0}</span>
            <span className="text-[10px] text-gray-500 whitespace-nowrap">/ {item.n_lines_total ?? 0}</span>
            {item.n_lines_at_risk > 0 && (
              <span className="text-[10px] text-red-700 whitespace-nowrap">✗ {item.n_lines_at_risk}</span>
            )}
            {item.n_lines_needs_purchase !== undefined && item.n_lines_needs_purchase > 0 && (
              <span className="text-[10px] text-amber-700 whitespace-nowrap">
                ⏳ {item.n_lines_needs_purchase}
              </span>
            )}
          </div>
        ),
      },
      {
        header: t('customerFulfillment.table.status'),
        accessor: 'fulfillment_status',
        cell: (_v: unknown, item: OrderSummary) => {
          const status: FulfillmentStatus = item.fulfillment_status
            ? item.fulfillment_status
            : item.at_risk
            ? 'AT_RISK'
            : item.needs_purchase
            ? 'NEEDS_PURCHASE_ON_TIME'
            : item.complies
            ? 'OK'
            : 'NOT_DETERMINABLE';

          if (status === 'OK') {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border bg-green-50 text-green-700 border-green-200 whitespace-nowrap">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('customerFulfillment.status.fulfilled')}
              </span>
            );
          }
          if (status === 'NEEDS_PURCHASE_ON_TIME') {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border bg-amber-50 text-amber-800 border-amber-200 whitespace-nowrap">
                <Clock3 className="w-3 h-3 mr-1" />
                {t('customerFulfillment.status.needsPurchase')}
              </span>
            );
          }
          if (status === 'NOT_DETERMINABLE') {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border bg-gray-50 text-gray-700 border-gray-200 whitespace-nowrap">
                {t('customerFulfillment.status.notDeterminable')}
              </span>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs border bg-red-50 text-red-700 border-red-200 whitespace-nowrap">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {t('customerFulfillment.status.atRisk')}
            </span>
          );
        },
      },
    ],
    [t]
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          title={t('customerFulfillment.kpis.evaluable')}
          value={kpis?.orders_evaluable ?? (loadingKpis ? '…' : 0)}
          isCount
          icon={<CheckCircle className="w-5 h-5" />}
          subtitle={t('customerFulfillment.kpis.evaluableSubtitle')}
        />
        <KpiCard
          title={t('customerFulfillment.kpis.atRisk')}
          value={kpis?.orders_at_risk ?? (loadingKpis ? '…' : 0)}
          isCount
          icon={<AlertTriangle className="w-5 h-5" />}
          subtitle={
            kpis
              ? `${kpis.orders_at_risk_pct}% ${t('customerFulfillment.kpis.ofEvaluable')}`
              : undefined
          }
        />
        <KpiCard
          title={t('customerFulfillment.kpis.needsPurchase')}
          value={kpis?.orders_needing_purchase ?? (loadingKpis ? '…' : 0)}
          isCount
          icon={<Clock3 className="w-5 h-5" />}
          subtitle={
            kpis && kpis.orders_needing_purchase_pct !== undefined
              ? `${kpis.orders_needing_purchase_pct}% ${t('customerFulfillment.kpis.ofEvaluable')}`
              : t('customerFulfillment.kpis.needsPurchaseSubtitle')
          }
        />
        <KpiCard
          title={t('customerFulfillment.kpis.overdueAtRisk')}
          value={kpis?.orders_overdue_at_risk ?? (loadingKpis ? '…' : 0)}
          isCount
          icon={<Clock className="w-5 h-5" />}
          subtitle={t('customerFulfillment.kpis.overdueSubtitle')}
        />
        <KpiCard
          title={t('customerFulfillment.kpis.customersAtRisk')}
          value={kpis?.customers_at_risk_count ?? (loadingKpis ? '…' : 0)}
          isCount
          icon={<Users className="w-5 h-5" />}
          subtitle={t('customerFulfillment.kpis.customersSubtitle')}
        />
      </div>

      {/* Top clientes en riesgo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#1681ba]" />
          {t('customerFulfillment.topCustomers.title')}
        </h3>
        {topCustomers.length === 0 ? (
          <div className="text-sm text-gray-500 py-2">
            {loadingKpis ? t('common.loading') : t('customerFulfillment.topCustomers.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[420px] w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-700">
                    {t('customerFulfillment.topCustomers.customer')}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('customerFulfillment.topCustomers.atRisk')}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('customerFulfillment.topCustomers.needsPurchase')}
                  </th>
                  <th className="text-right py-2 px-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('customerFulfillment.topCustomers.overdue')}
                  </th>
                  <th className="text-right py-2 pl-2 font-medium text-gray-700 whitespace-nowrap">
                    {t('customerFulfillment.topCustomers.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((c) => (
                  <tr key={c.customer} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900 truncate" title={c.customer}>
                      {c.customer}
                    </td>
                    <td className="text-right py-2 px-2 tabular-nums text-red-700 font-medium">
                      {c.orders_at_risk}
                    </td>
                    <td className="text-right py-2 px-2 tabular-nums text-amber-700">
                      {c.orders_needing_purchase ?? 0}
                    </td>
                    <td className="text-right py-2 px-2 tabular-nums text-amber-700">
                      {c.orders_overdue}
                    </td>
                    <td className="text-right py-2 pl-2 tabular-nums text-gray-900">
                      {c.orders_total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filtros */}
      <FilterBar
        filters={filterOptions}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClearFilter={() => setRiskFilter('attention')}
        onClearAllFilters={() => setRiskFilter('attention')}
      />

      {/* Listado */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            {t('customerFulfillment.table.title')}
          </h2>
          <div className="w-full md:w-[320px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('customerFulfillment.filters.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba]"
            />
          </div>
        </div>

        <div className="flex justify-end items-center mb-4">
          <div className="text-sm text-gray-500">
            {totalCount} {t('customerFulfillment.table.total')}
          </div>
        </div>

        <div className="relative">
          {loadingList && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]" />
            </div>
          )}
          <DataTable
            columns={columns}
            data={rows}
            compact
            externalPagination={{
              currentPage: page,
              totalPages,
              totalCount,
              pageSize,
              onPageChange: (p) => fetchPage(p),
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPage(1);
                void fetchPage(1, size);
              },
            }}
            pagination
            onRowClick={(item: OrderSummary) => {
              setSelectedSalesOrderId(item.sales_order_id);
              setSidebarOpen(true);
            }}
          />
        </div>
      </div>

      <CustomerFulfillmentDetailSidebar
        salesOrderId={selectedSalesOrderId}
        warehouseId={warehouseId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
    </div>
  );
}
